import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Typography,
  Input,
  Button,
  List,
  Tag,
  Dropdown,
  Flex,
  Grid,
  theme,
  message,
  Modal,
  MenuProps,
  Card,
  Pagination,
  Space,
  Skeleton,
} from 'antd';
import { PlusOutlined, EllipsisOutlined, LeftOutlined } from '@ant-design/icons';
import { NewFlow } from './AssistantFlowNew/AssistantFlowNew';
import { AssistantProcessFlow } from './AssistantFlowNew/AssistantProcessFlow/AssistantProcessFlow';
import { useAccount } from '../../../../store';
import { useAssistant } from '../../../../contexts/AssistantContext';
import { useFlows } from '../../hooks/useFlows';
import { Flow, FlowStatus } from '../../types';
import { useAuthGuard } from '../../../../hooks/useAuthGuard';
import { AppBreadcrumb, useBreadcrumb } from '../../../breadcrumb';

const { Content } = Layout;
const { Title, Text, Link } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

interface ConversationFlowsProps {
  onButtonClick?: (newComponent: React.ReactNode) => void;
}

export const ConversationFlows: React.FC<ConversationFlowsProps> = ({ onButtonClick }) => {
  useAuthGuard(); // Proteger esta página con autenticación
  
  const navigate = useNavigate();
  const { currentAccount } = useAccount();
  const { currentAssistant } = useAssistant();
  const chatbotId = currentAssistant?.id || '';

  const {
    flowData,
    selectedFlow,
    conversationData,
    actionsData,
    isLoading,
    handleFlowClick,
    handleDeleteFlow,
    handleDuplicateFlow: duplicateFlow,
    handleUpdateFlowStatus,
    fetchFlows
  } = useFlows(chatbotId);

  const [currentView, setCurrentView] = useState<'list' | 'new' | 'edit'>('list');
  const [isNewFlowModalOpen, setIsNewFlowModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { token } = useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const pageSize = 8; // Cantidad de flows por página

  // Add useEffect to handle view state changes
  React.useEffect(() => {
    if (selectedFlow && selectedFlow.id) {
      setCurrentView('edit');
    }
  }, [selectedFlow]);

  const handleCreateNewFlow = () => {
    // setIsNewFlowModalOpen(true);
    handleFlowTypeSelect('conversational');
  };

  const handleFlowTypeSelect = (type: 'conversational' | 'process') => {
    setIsNewFlowModalOpen(false);
    setCurrentView('new');
    // Set the selected flow type
    const newFlow: Flow = {
      id: '',
      name: '',
      description: '',
      status: 0,
      type: type,
      chatbotId: chatbotId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    handleFlowClick(newFlow);
  };

  const handleReturnToList = () => {
    setCurrentView('list');
    fetchFlows();
  };

  const confirmDeleteFlow = (flow: Flow) => {
    Modal.confirm({
      title: `Delete flow "${flow.name}"?`,
      content: 'This will permanently delete the flow and its associated conversations.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => handleDeleteFlow(flow),
    });
  };

  const handleDuplicateFlowById = (flowId: string) => {
    const flow = flowData.find(f => f.id === flowId);
    if (flow) {
      duplicateFlow(flow);
    }
  };

  const [filteredFlowData, setFilteredFlowData] = useState<Flow[]>(flowData);

  React.useEffect(() => {
    let filtered = flowData;

    if (searchValue) {
      const term = searchValue.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(term) || item.description.toLowerCase().includes(term),
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((item) => {
        const status = item.status;
        if (statusFilter === 'active') return status === 1 || status === 'active';
        if (statusFilter === 'draft') return status === 0 || status === 'draft';
        if (statusFilter === 'archive') return status === 2 || status === 'archive';
        return true;
      });
    }

    setFilteredFlowData(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchValue, flowData, statusFilter]);

  // Calculate current data to display based on pagination
  const paginatedFlowData = filteredFlowData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderSkeletonFlowCard = () => (
    <Card style={{ marginBottom: '12px' }}>
      <List.Item>
        <Flex
          vertical
          gap={token.marginXS}
          style={{ paddingRight: isMobile ? token.padding : 0, width: '100%' }}
        >
          <Flex
            align="center"
            justify="space-between"
            wrap={isMobile ? 'wrap' : 'nowrap'}
            gap={token.marginXS}
          >
            <Skeleton.Input style={{ width: 200, height: 24 }} active />
            <Skeleton.Button style={{ width: 60, height: 22, borderRadius: 11 }} active />
          </Flex>
          <Skeleton.Input style={{ width: '80%', height: 16 }} active />
          <Flex
            gap={isMobile ? token.marginXS : token.marginLG}
            wrap="wrap"
            vertical={!screens.sm}
            style={{ marginTop: token.marginXS }}
          >
            <Skeleton.Input style={{ width: 120, height: 14 }} active />
          </Flex>
        </Flex>
        <div style={{ position: 'absolute', top: token.paddingSM, right: token.paddingSM }}>
          <Skeleton.Button style={{ width: 32, height: 32 }} active />
        </div>
      </List.Item>
    </Card>
  );

  // Determinar breadcrumb adicional según la vista
  const getAdditionalBreadcrumbs = () => {
    if (currentView === 'new') {
      return [{
        label: 'New Flow',
        onClick: () => handleReturnToList(),
        isActive: true
      }];
    } else if (currentView === 'edit' && selectedFlow) {
      return [{
        label: selectedFlow.name || 'Edit Flow',
        onClick: () => handleReturnToList(),
        isActive: true
      }];
    }
    return [];
  };

  const breadcrumbItems = useBreadcrumb({
    section: 'Flows',
    includeAssistant: true,
    additionalItems: getAdditionalBreadcrumbs(),
    onSectionClick: currentView !== 'list' ? handleReturnToList : undefined
  });

  // Renderizado condicional basado en la vista actual
  if (currentView === 'new') {
    if (selectedFlow?.type === 'process') {
      return (
        <>
          <AppBreadcrumb items={breadcrumbItems} />
          <AssistantProcessFlow
            onReturnButtonClick={handleReturnToList}
            mode="create"
            assistantId={chatbotId}
          />
        </>
      );
    }
    return (
      <>
        <AppBreadcrumb items={breadcrumbItems} />
        <NewFlow
          onReturnButtonClick={handleReturnToList}
          mode="create"
          conversationData={{ messages: [] }}
        />
      </>
    );
  }

  if (currentView === 'edit' && selectedFlow) {
    if (selectedFlow.type === 'process') {
      return (
        <>
          <AppBreadcrumb items={breadcrumbItems} />
          <AssistantProcessFlow
            onReturnButtonClick={handleReturnToList}
            mode="edit"
            flowData={selectedFlow}
            assistantId={chatbotId}
          />
        </>
      );
    }
    return (
      <>
        <AppBreadcrumb items={breadcrumbItems} />
        <NewFlow
          onReturnButtonClick={handleReturnToList}
          mode="edit"
          flowData={selectedFlow}
          conversationData={conversationData}
          actionsData={actionsData}
        />
      </>
    );
  }

  return (
    <>
      <AppBreadcrumb items={breadcrumbItems} />
      <Card>
        <Layout>
          <Content
            style={{
              background: token.colorBgContainer,
            }}
          >
        <Flex vertical gap={isMobile ? token.marginMD : token.marginLG}>
          <Flex
            justify="space-between"
            align="center"
            wrap="wrap"
            gap={token.marginSM}
            vertical={isMobile}
          >
            <Title level={4} style={{ margin: 0 }}>
              Conversation Flows
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateNewFlow}
              style={{ width: isMobile ? '100%' : 'auto' }}
              id="create-new-flow-button"
            >
              Create new flow
            </Button>
          </Flex>

          <Flex gap={token.marginSM} wrap="wrap" style={{ width: '60%' }}>
            <Input.Search
              placeholder="Search"
              style={{
                flex: 1,
                minWidth: '200px',
                width: isMobile ? '100%' : 'auto',
                marginBottom: isMobile ? token.marginXS : 0,
              }}
              onSearch={(value) => setSearchValue(value)}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <Space wrap className="process-flow-filters">
              {(['all', 'active', 'draft'] as const).map(filter => (
                <Button 
                  key={filter}
                  type="default"
                  onClick={() => setStatusFilter(filter)}
                  style={{
                    borderColor: statusFilter === filter ? 'var(--ant-colorSecondary)' : undefined,
                    color: statusFilter === filter ? 'var(--ant-colorSecondary)' : undefined,
                    backgroundColor: 'transparent'
                  }}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </Button>
              ))}
            </Space>
          </Flex>

          <List
            itemLayout="vertical"
            dataSource={isLoading ? Array(pageSize).fill({}) : paginatedFlowData}
            renderItem={(item) => {
              // Show skeleton if loading or item data is incomplete
              if (isLoading || !item.name || !item.id) {
                return renderSkeletonFlowCard();
              }
              const dropdownItems: MenuProps['items'] = [
                {
                  key: 'duplicate',
                  label: 'Duplicate flow',
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    handleDuplicateFlowById(item.id);
                  },
                },
                {
                  key: 'toggleStatus',
                  label: item.status === 1 || item.status === 'active' ? 'Set as Draft' : 'Active flow',
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    const newStatus = item.status === 1 || item.status === 'active' ? 0 : 1; // Set to Draft if currently Active, else set to Active
                    handleUpdateFlowStatus(item, newStatus, chatbotId);
                  },
                  style: {
                    color: item.status === 1 || item.status === 'active' ? token.colorWarning : token.colorSuccess,
                  },
                },
/*                 {
                  key: 'archive',
                  label: 'Archive flow',
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    handleUpdateFlowStatus(item, 2, chatbotId); // Set status to Archive
                  },
                }, */
                {
                  key: 'delete',
                  label: 'Delete flow',
                  onClick: (e) => {
                    e.domEvent.stopPropagation();
                    confirmDeleteFlow(item);
                  },
                  style: { color: token.colorError },
                },
              ];

              return (
                <Card style={{ marginBottom: '12px' }}>
                  <List.Item
                    onClick={() => handleFlowClick(item)}
                    style={{ cursor: 'pointer', marginBottom: '10px' }}
                    extra={
                      <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown menu={{ items: dropdownItems }} trigger={['click']}>
                          <Button
                            icon={<EllipsisOutlined />}
                            style={{
                              position: isMobile ? 'absolute' : 'static',
                              top: isMobile ? token.paddingSM : 'auto',
                              right: isMobile ? token.paddingSM : 'auto',
                            }}
                          />
                        </Dropdown>
                      </div>
                    }
                  >
                    <Flex
                      vertical
                      gap={token.marginXS}
                      style={{ paddingRight: isMobile ? token.padding : 0 }}
                    >
                      <Flex
                        align="center"
                        justify="space-between"
                        wrap={isMobile ? 'wrap' : 'nowrap'}
                        gap={token.marginXS}
                      >
                        <Title level={5} style={{ margin: 0 }}>
                          {item.name}
                        </Title>
                        <Tag
                          color={
                            item.status === 1 || item.status === 'active'
                              ? 'success'
                              : item.status === 2 || item.status === 'archive'
                                ? 'default'
                                : 'warning'
                          }
                        >
                          {item.status === 0 || item.status === 'draft'
                            ? 'Draft'
                            : item.status === 1 || item.status === 'active'
                              ? 'Active'
                              : 'Archive'}
                        </Tag>
                      </Flex>
                      <Text type="secondary">{item.description}</Text>
                      <Flex
                        gap={isMobile ? token.marginXS : token.marginLG}
                        wrap="wrap"
                        vertical={!screens.sm}
                        style={{ marginTop: token.marginXS }}
                      >
                        {/* <Text type="secondary">Flow ID: {item.id}</Text> */}
                        <Text type="secondary">
                          Updated: {new Date(Number(item.updatedAt) * 1000).toLocaleDateString()}
                        </Text>
                      </Flex>
                    </Flex>
                  </List.Item>
                </Card>
              );
            }}
          />

          {!isLoading && (
            <Flex justify="end" >
              <Pagination
                current={currentPage}
                onChange={handlePageChange}
                pageSize={pageSize}
                total={filteredFlowData.length}
                showSizeChanger={false}
              />
            </Flex>
          )}
        </Flex>
      </Content>

    </Layout>
    </Card>
    </>
  );
};
